/** Connects the BrowserPanel to the new per-user browser-harness instead of
 *  the legacy single-sidecar /browser/stream endpoint. Opt-in: calls the
 *  backend's /browser/service-session first, falls back to legacy when the
 *  service isn't configured.
 *
 *  The wire protocol on the service's /stream is intentionally the same
 *  shape as the legacy endpoint so BrowserPanel can swap hooks without UI
 *  changes. First WS message: {type:'auth', token, viewport}.
 *
 *  This hook mirrors useBrowserStream's return surface exactly so callers
 *  can be feature-flagged in a single useMemo/useBrowserStreamImpl() switch.
 *
 *  StrictMode-safe singleton: in dev, React mounts every component twice
 *  to surface side-effect bugs. The pre-singleton implementation opened a
 *  fresh WS per-mount-effect, which produced two simultaneous /stream
 *  upgrades to the service; one would send `auth` first while the other
 *  raced a `resize` from a viewport observer and got 4401-closed. We now
 *  hold a single module-level WebSocket and ref-count hook subscribers,
 *  with a 500ms teardown grace so the StrictMode unmount/remount cycle
 *  doesn't churn through reconnects. With this in place the service-side
 *  pre-auth tolerance becomes belt-and-suspenders. */

import { useEffect, useCallback, useState } from 'react';
import { API_BASE } from '@/lib/config';
import { useAuthStore } from '@/stores/authStore';
import { useBrowserStore } from '@/stores/browserStore';

interface FrameTimestamp { sentAt: number; receivedAt: number; }

interface ServiceSessionHandshake {
  enabled: boolean;
  wsUrl?: string;
  token?: string;
  sessionId?: string;
  viewport?: { width: number; height: number };
}

/** `enabled: false` only when the backend says the harness isn't configured.
 *  A failed request — the harness restarting mid-deploy, the backend down —
 *  throws, and the caller retries. */
async function fetchSessionHandshake(jwt: string | null): Promise<ServiceSessionHandshake> {
  if (!jwt) return { enabled: false };
  const res = await fetch(`${API_BASE}/browser/service-session`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) throw new Error(`service-session ${res.status}`);
  return (await res.json()) as ServiceSessionHandshake;
}

/** 1 s, 2 s, 4 s … capped at 15 s; reset once a socket opens. */
function nextRetryDelay(): number {
  const delay = Math.min(15_000, 1000 * 2 ** shared.failedAttempts);
  shared.failedAttempts += 1;
  return delay;
}

// ---------------------------------------------------------------------------
// Module-level shared connection. Only one WebSocket per browser tab —
// refcount tracks how many hook instances need it, with a teardown grace
// to absorb StrictMode double-mount without churning reconnects.
// ---------------------------------------------------------------------------

type Listener = (event: ListenerEvent) => void;
type ListenerEvent =
  | { type: 'connected'; sessionId: string | null }
  | { type: 'disconnected' }
  | { type: 'enabled'; enabled: boolean };

const shared = {
  ws: null as WebSocket | null,
  reconnectTimer: undefined as ReturnType<typeof setTimeout> | undefined,
  teardownTimer: undefined as ReturnType<typeof setTimeout> | undefined,
  refCount: 0,
  enabled: true,
  sessionId: null as string | null,
  isConnected: false,
  frameTimes: [] as FrameTimestamp[],
  listeners: new Set<Listener>(),
  connecting: false,
  failedAttempts: 0,
};

function notify(event: ListenerEvent) {
  for (const listener of shared.listeners) listener(event);
}

function clearTimers() {
  if (shared.reconnectTimer) { clearTimeout(shared.reconnectTimer); shared.reconnectTimer = undefined; }
  if (shared.teardownTimer) { clearTimeout(shared.teardownTimer); shared.teardownTimer = undefined; }
}

async function ensureConnection(): Promise<void> {
  // Cancel a pending teardown — a hook subscriber arrived during the grace
  // period (typical StrictMode unmount-remount cycle).
  if (shared.teardownTimer) {
    clearTimeout(shared.teardownTimer);
    shared.teardownTimer = undefined;
  }

  if (shared.ws && (shared.ws.readyState === WebSocket.OPEN || shared.ws.readyState === WebSocket.CONNECTING)) {
    return;
  }
  if (shared.connecting) return;
  shared.connecting = true;

  try {
    const handshake = await fetchSessionHandshake(useAuthStore.getState().token);
    if (!handshake.enabled || !handshake.wsUrl || !handshake.token) {
      console.warn('[browser-harness] handshake not usable, giving up', handshake);
      shared.enabled = false;
      notify({ type: 'enabled', enabled: false });
      return;
    }
    shared.enabled = true;
    shared.sessionId = handshake.sessionId ?? null;
    notify({ type: 'enabled', enabled: true });

    const ws = new WebSocket(handshake.wsUrl);
    shared.ws = ws;

    ws.onopen = () => {
      shared.failedAttempts = 0;
      const initialViewport = useBrowserStore.getState().viewport;
      const authPayload = { type: 'auth', token: handshake.token, viewport: initialViewport };
      ws.send(JSON.stringify(authPayload));
      shared.isConnected = true;
      shared.frameTimes = [];
      useBrowserStore.getState().setConnected(true);
      notify({ type: 'connected', sessionId: shared.sessionId });
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data) as Record<string, unknown>;
        const store = useBrowserStore.getState();
        switch (msg.type) {
          // init (once, after auth) and status (on lease, URL and viewport
          // changes) carry the same state of the shared tab.
          case 'init':
          case 'status':
            if (typeof msg.url === 'string') store.setCurrentUrl(msg.url);
            if (msg.viewport && typeof msg.viewport === 'object') {
              const v = msg.viewport as { width: number; height: number };
              store.setViewport(v);
            }
            store.setLeasedBy((msg.lease as { owner?: string } | null)?.owner ?? null);
            break;
          case 'frame':
            if (typeof msg.data === 'string') {
              store.setLatestFrame(`data:image/jpeg;base64,${msg.data}`);
              // The harness only sends the next frame once this one is acked
              // (browser-harness/src/frame-gate.ts) — keeps the picture live
              // on slow links instead of seconds behind.
              ws.send(JSON.stringify({ type: 'frame-ack' }));
              const ts = typeof msg.timestamp === 'number' ? msg.timestamp : Date.now();
              const now = Date.now();
              const win = shared.frameTimes;
              win.push({ sentAt: ts, receivedAt: now });
              if (win.length > 60) win.shift();
              if (win.length >= 2) {
                const first = win[0]!; const last = win[win.length - 1]!;
                const elapsed = (last.receivedAt - first.receivedAt) / 1000;
                const currentFps = elapsed > 0 ? (win.length - 1) / elapsed : 0;
                const latencies = win.map((f) => f.receivedAt - f.sentAt);
                const avg = latencies.reduce((a, b) => a + b, 0) / latencies.length;
                store.setStats(Math.round(currentFps), Math.round(avg));
              }
            }
            break;
          case 'error':
            console.warn('[browser-harness] error:', msg.error);
            break;
        }
      } catch (err) {
        console.error('[browser-harness] parse error:', err);
      }
    };

    ws.onclose = (event) => {
      console.warn('[browser-harness] WS closed', { code: event.code, reason: event.reason, wasClean: event.wasClean });
      shared.isConnected = false;
      shared.ws = null;
      useBrowserStore.getState().setConnected(false);
      // The next init says who holds the tab; don't show a stale driver.
      useBrowserStore.getState().setLeasedBy(null);
      notify({ type: 'disconnected' });

      // Reconnect whenever a hook is still subscribed — otherwise the user
      // navigated away (or StrictMode is mid-teardown) and nobody is
      // listening. Clean closes count too: the harness closes with 1000 when
      // it replaces a session (dead tab, idle evict, cap relief) and 1012
      // when it restarts (it adopts the same container on boot), and the
      // panel froze on its last frame because only unclean closes retried.
      // Our own teardown only closes at refCount 0, so it never loops here.
      if (shared.refCount > 0) {
        shared.reconnectTimer = setTimeout(() => { void ensureConnection(); }, event.wasClean ? 500 : 2000);
      }
    };

    ws.onerror = (ev) => { console.error('[browser-harness] WS error', ev); };
  } catch (err) {
    console.warn('[browser-harness] connect failed, retrying', err);
    shared.isConnected = false;
    useBrowserStore.getState().setConnected(false);
    if (shared.refCount > 0) {
      shared.reconnectTimer = setTimeout(() => { void ensureConnection(); }, nextRetryDelay());
    }
  } finally {
    shared.connecting = false;
  }
}

function teardownNow() {
  clearTimers();
  if (shared.ws) {
    try { shared.ws.close(); } catch { /* ignore */ }
    shared.ws = null;
  }
  shared.isConnected = false;
  shared.frameTimes = [];
  useBrowserStore.getState().setConnected(false);
  notify({ type: 'disconnected' });
}

function release() {
  shared.refCount = Math.max(0, shared.refCount - 1);
  if (shared.refCount > 0) return;
  // 500ms grace covers the StrictMode unmount-remount window without
  // churning the WebSocket. If a new subscriber registers before this
  // fires, ensureConnection cancels the timer.
  shared.teardownTimer = setTimeout(() => {
    if (shared.refCount === 0) teardownNow();
  }, 500);
}

export interface BrowserHarnessStreamReturn {
  isConnected: boolean;
  enabled: boolean;
  sessionId: string | null;
  currentUrl: string;
  latestFrame: string;
  fps: number;
  latency: number;
  viewport: { width: number; height: number };
  connect: () => void;
  disconnect: () => void;
  send: (msg: object) => void;
}

export function useBrowserHarnessStream(): BrowserHarnessStreamReturn {
  const [isConnected, setIsConnected] = useState(shared.isConnected);
  const [enabled, setEnabled] = useState(shared.enabled);
  const [sessionId, setSessionId] = useState<string | null>(shared.sessionId);

  const currentUrl = useBrowserStore((s) => s.currentUrl);
  const latestFrame = useBrowserStore((s) => s.latestFrame);
  const fps = useBrowserStore((s) => s.fps);
  const latency = useBrowserStore((s) => s.latency);
  const viewport = useBrowserStore((s) => s.viewport);

  useEffect(() => {
    const listener: Listener = (event) => {
      if (event.type === 'connected') {
        setIsConnected(true);
        setSessionId(event.sessionId);
      } else if (event.type === 'disconnected') {
        setIsConnected(false);
      } else if (event.type === 'enabled') {
        setEnabled(event.enabled);
      }
    };
    shared.listeners.add(listener);
    return () => { shared.listeners.delete(listener); };
  }, []);

  const connect = useCallback(() => {
    shared.refCount += 1;
    void ensureConnection();
  }, []);

  const disconnect = useCallback(() => {
    release();
  }, []);

  const send = useCallback((msg: object) => {
    if (shared.ws?.readyState === WebSocket.OPEN) {
      shared.ws.send(JSON.stringify(msg));
    }
  }, []);

  // Auto-cleanup on unmount: if connect() was called, decrement the refcount.
  // The grace timer in release() absorbs StrictMode's unmount-remount cycle.
  useEffect(() => {
    return () => {
      // We can't tell here whether connect() was called for THIS instance,
      // so we don't auto-decrement. Callers that called connect() must call
      // disconnect() in their own cleanup. BrowserPanel does this already.
    };
  }, []);

  return {
    isConnected,
    enabled,
    sessionId,
    currentUrl,
    latestFrame,
    fps,
    latency,
    viewport,
    connect,
    disconnect,
    send,
  };
}
